import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchSchema,
  fetchTableRows,
  fetchForeignKeyOptions,
  fetchRoutines,
  setSelectedTable,
  selectSchema,
  selectSelectedTable,
  selectCurrentTableData,
  selectLoading,
  selectError,
  selectIsRowModalOpen,
  selectIsDeleteConfirmOpen,
  selectIsRoutineModalOpen,
  clearError
} from '../../features/adminSlice';
import AdminTable from './AdminTable';
import AdminRowModal from './AdminRowModal';
import AdminDeleteConfirm from './AdminDeleteConfirm';
import AdminRoutineList from './AdminRoutineList';
import AdminRoutineModal from './AdminRoutineModal';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [mode, setMode] = useState('tables');

  const schema = useSelector(selectSchema);
  const selectedTable = useSelector(selectSelectedTable);
  const tableData = useSelector(selectCurrentTableData);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const isRowModalOpen = useSelector(selectIsRowModalOpen);
  const isDeleteConfirmOpen = useSelector(selectIsDeleteConfirmOpen);
  const isRoutineModalOpen = useSelector(selectIsRoutineModalOpen);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  // Fetch schema then FK lookup options (FK tables are derived from schema's fk_config)
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchSchema()).then(() => dispatch(fetchForeignKeyOptions()));
    }
  }, [dispatch, isAuthenticated]);

  // Fetch table data when selected table changes
  useEffect(() => {
    if (isAuthenticated && selectedTable) {
      dispatch(fetchTableRows(selectedTable));
    }
  }, [dispatch, selectedTable, isAuthenticated]);

  // Fetch routines when switching to routines mode
  useEffect(() => {
    if (isAuthenticated && mode === 'routines') {
      dispatch(fetchRoutines());
    }
  }, [dispatch, isAuthenticated, mode]);

  // Handle table selection change
  const handleTableChange = (e) => {
    dispatch(setSelectedTable(e.target.value));
  };

  // Navigate back to home
  const handleBack = () => {
    navigate('/');
  };

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Authentication Required</h2>
          <p className="text-gray-400 mb-6">Please sign in to access the admin dashboard.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gradient-to-r from-[#44C2FD] to-[#23A6D5] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1A] text-white">
      {/* Header */}
      <div className="bg-[#111827] border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              title="Back to Home"
            >
              <span className="material-icons">arrow_back</span>
            </button>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          </div>

          {/* Mode toggle + table selector */}
          <div className="flex items-center gap-4">
            {mode === 'tables' && (
              <>
                <label className="text-gray-400">Table:</label>
                <select
                  value={selectedTable}
                  onChange={handleTableChange}
                  className="bg-[#1F2937] border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#44C2FD] cursor-pointer"
                >
                  {schema?.tables?.map((table) => (
                    <option key={table} value={table}>
                      {table}
                    </option>
                  ))}
                </select>
              </>
            )}
            <div className="flex rounded-lg overflow-hidden border border-gray-700">
              {['tables', 'routines'].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${mode === m
                      ? 'bg-[#44C2FD] text-white'
                      : 'bg-[#1F2937] text-gray-400 hover:text-white'
                    }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 text-red-200 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="material-icons">error</span>
              <span>{error}</span>
            </div>
            <button
              onClick={() => dispatch(clearError())}
              className="p-1 hover:bg-red-800 rounded transition-colors"
            >
              <span className="material-icons">close</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading && (mode === 'tables' ? !tableData : true) ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#44C2FD]"></div>
          </div>
        ) : mode === 'tables' ? (
          <AdminTable
            schema={schema?.schema?.[selectedTable] || []}
            primaryKeys={schema?.primary_keys?.[selectedTable] || ['id']}
            data={tableData?.rows || []}
            tableName={selectedTable}
          />
        ) : (
          <AdminRoutineList />
        )}
      </div>

      {/* Modals */}
      {isRowModalOpen && (
        <AdminRowModal
          schema={schema?.schema?.[selectedTable] || []}
          primaryKeys={schema?.primary_keys?.[selectedTable] || ['id']}
          tableName={selectedTable}
        />
      )}

      {isDeleteConfirmOpen && (
        <AdminDeleteConfirm
          tableName={selectedTable}
          primaryKeys={schema?.primary_keys?.[selectedTable] || ['id']}
        />
      )}

      {isRoutineModalOpen && <AdminRoutineModal />}
    </div>
  );
};

export default AdminDashboard;
